import { Link } from 'react-router-dom';
import { Activity, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useListeningRulesHealthQuery } from '@/client/api/marketing/sources';
import type { ListeningRuleHealthState } from '@/client/types/marketing/api';

const STATE_LABEL: Record<ListeningRuleHealthState, string> = {
  ok: 'Ok',
  dry: 'Dry',
  blind: 'Blind',
  never_run: 'Never run',
  disabled: 'Disabled',
};

const STATE_TONE: Record<ListeningRuleHealthState, string> = {
  ok: 'bg-emerald-50 text-emerald-800 border-emerald-100',
  dry: 'bg-amber-50 text-amber-900 border-amber-100',
  blind: 'bg-red-50 text-red-800 border-red-100',
  never_run: 'bg-gray-50 text-gray-700 border-gray-200',
  disabled: 'bg-gray-100 text-gray-500 border-gray-200',
};

function formatCadence(lagMinutes: number | null, intervalMinutes: number | null): string {
  if (lagMinutes == null) return '—';
  if (intervalMinutes != null && lagMinutes < intervalMinutes) {
    const due = Math.max(0, Math.round(intervalMinutes - lagMinutes));
    return due === 0 ? 'due now' : `due in ${due}m`;
  }
  if (lagMinutes < 60) return `${Math.round(lagMinutes)}m overdue`;
  const hours = lagMinutes / 60;
  if (hours < 48) return `${hours.toFixed(1)}h overdue`;
  return `${Math.round(hours / 24)}d overdue`;
}

/**
 * Compact health strip for Listening. Surfaces ok/dry/blind without drowning
 * the Auto-pull panel. Prefer lag vs interval over raw timestamps.
 */
export function ListeningHealthStrip() {
  const { data, isLoading, isError } = useListeningRulesHealthQuery();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-gray-100 bg-white px-4 py-2.5 text-xs text-gray-500 shadow-sm">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Checking listening health…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-xl border border-amber-100 bg-amber-50/80 px-4 py-2.5 text-xs text-amber-900">
        Listening health unavailable — rules may still sync; the health endpoint did not respond.
      </div>
    );
  }

  const states = (Object.keys(STATE_LABEL) as ListeningRuleHealthState[]).filter(
    (s) => (data.states[s] ?? 0) > 0,
  );
  const worst = data.items
    .filter((i) => i.state === 'blind' || i.state === 'dry')
    .slice(0, 3);

  return (
    <div className="rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Activity className="h-4 w-4 text-gray-400" />
        <p className="text-sm font-semibold text-gray-900">Listening health</p>
        <span className="text-xs text-gray-400">{data.count} rules</span>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {states.length === 0 ? (
            <span className="text-xs text-gray-400">No state counts yet</span>
          ) : (
            states.map((s) => (
              <span
                key={s}
                className={cn(
                  'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
                  STATE_TONE[s],
                )}
              >
                {STATE_LABEL[s]} {data.states[s]}
              </span>
            ))
          )}
        </div>
      </div>
      {worst.length > 0 && (
        <ul className="mt-2 space-y-1 border-t border-gray-50 pt-2">
          {worst.map((item) => (
            <li
              key={item.lrid}
              className="flex flex-wrap items-baseline justify-between gap-2 text-xs text-gray-600"
            >
              <span className="font-medium text-gray-800">{item.name}</span>
              <span className="text-gray-400">
                {STATE_LABEL[item.state]}
                {item.lag_minutes != null
                  ? ` · ${formatCadence(item.lag_minutes, item.interval_minutes)}`
                  : ''}
                {item.interval_minutes != null ? ` · every ${item.interval_minutes}m` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-[11px] text-gray-400">
        Dry = runs but no matches. Blind = overdue vs interval.{' '}
        <Link to="/admin/marketing/radar/overview" className="font-medium text-blue-700 hover:underline">
          Open Radar
        </Link>
      </p>
    </div>
  );
}
